/**
 * Pure helpers for marking which pitcher throws in which tournament game. Shared by the Build
 * Itinerary wizard's Pitchers step and the tournament games panel.
 */

import type { GamePitcher, TournamentGameDto } from "../../../../types/tournament-games";

/** Position tokens that mean the player pitches. Two-way and staff roles included. */
const PITCHER_TOKENS = new Set(["P", "PITCHER", "RHP", "LHP", "SP", "RP", "TWP"]);

/**
 * Whether a free-text position names a pitcher. Split on "/", ",", "-" and whitespace, so
 * "RHP/OF", "OF, P" and "Right Handed Pitcher" count and "OF" or "SS" does not.
 * Case-insensitive. A blank position names nothing, so it is false here.
 */
export function isPitcherPositionText(position: string | null | undefined): boolean {
  if (!position) return false;
  return position
    .split(/[/,\s-]+/)
    .some((token) => PITCHER_TOKENS.has(token.trim().toUpperCase()));
}

/**
 * Whether a matched player may be offered as a pitcher. An unknown position (missing, null or
 * blank) is not proof they do not pitch, so they stay. Only an explicit position with no pitcher
 * token ("OF", "Outfield", "Utility") rules a player out.
 */
export function isPitcherCandidatePosition(position: string | null | undefined): boolean {
  if (!position || position.trim() === "") return true;
  return isPitcherPositionText(position);
}

/**
 * The prospects a coach can mark as pitching in a game: the followed pitchers on its two teams
 * (deduped by prospect id), plus anyone already marked so a saved mark never drops out of view.
 * A followed outfielder is not offered; a player with no position recorded is.
 * Order: matched players as the API sent them, then any extra saved pitchers.
 */
export function pitcherCandidates(
  game: Pick<TournamentGameDto, "matchedPlayers" | "pitchers">,
): GamePitcher[] {
  const seen = new Set<string>();
  const out: GamePitcher[] = [];
  const add = (p: GamePitcher) => {
    if (seen.has(p.prospectId)) return;
    seen.add(p.prospectId);
    out.push({ prospectId: p.prospectId, name: p.name });
  };
  for (const p of game.matchedPlayers ?? []) {
    if (isPitcherCandidatePosition(p.position)) add(p);
  }
  for (const p of game.pitchers ?? []) add(p);
  return out;
}

/** The game's saved pitcher ids. */
export function pitcherIds(game: Pick<TournamentGameDto, "pitchers">): string[] {
  return (game.pitchers ?? []).map((p) => p.prospectId);
}

/** Order-insensitive equality of two id lists. */
export function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/** Add or remove an id from a list, keeping the rest in order. */
export function toggleId(ids: readonly string[], id: string, on: boolean): string[] {
  const without = ids.filter((x) => x !== id);
  return on ? [...without, id] : without;
}
